import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import { Google, Link as LinkIcon, LinkOff } from '@mui/icons-material';
import { oauthService, LinkedProviders } from '@/services/oauthService';

const LinkedAccounts: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [providers, setProviders] = useState<LinkedProviders | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check for success message from OAuth redirect
  useEffect(() => {
    const linked = searchParams.get('linked');
    if (linked) {
      setSuccess(`Successfully linked your ${linked} account!`);
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  // Load linked providers
  useEffect(() => {
    const loadProviders = async () => {
      try {
        const data = await oauthService.getLinkedProviders();
        setProviders(data);
      } catch (err) {
        // OAuth might not be enabled
        setProviders(null);
      } finally {
        setLoading(false);
      }
    };
    loadProviders();
  }, []);

  const handleLink = (provider: 'google') => {
    setActionLoading(provider);
    setError(null);
    oauthService.linkGoogle();
  };

  const handleUnlink = async (provider: 'google') => {
    setActionLoading(provider);
    setError(null);
    setSuccess(null);

    try {
      await oauthService.unlinkProvider(provider);
      setSuccess(`Successfully unlinked your ${provider} account`);

      // Reload providers
      const data = await oauthService.getLinkedProviders();
      setProviders(data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || `Failed to unlink ${provider}`);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // If OAuth is not configured, don't show this section
  if (!providers || !providers.available.google.enabled) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Linked Accounts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect your social accounts for faster sign-in
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <List disablePadding>
          {providers.available.google.enabled && (
            <ListItem
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
              }}
            >
              <ListItemIcon>
                <Google sx={{ color: '#4285F4' }} />
              </ListItemIcon>
              <ListItemText
                primary="Google"
                secondary={providers.linked.google ? 'Connected' : 'Not connected'}
              />
              <ListItemSecondaryAction>
                {providers.linked.google ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="Linked"
                      color="success"
                      size="small"
                      icon={<LinkIcon />}
                    />
                    <Button
                      size="small"
                      color="error"
                      startIcon={actionLoading === 'google' ? <CircularProgress size={16} /> : <LinkOff />}
                      onClick={() => handleUnlink('google')}
                      disabled={actionLoading !== null}
                    >
                      Unlink
                    </Button>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={actionLoading === 'google' ? <CircularProgress size={16} /> : <LinkIcon />}
                    onClick={() => handleLink('google')}
                    disabled={actionLoading !== null}
                  >
                    Link
                  </Button>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          )}
        </List>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Note: You can only sign in with OAuth after linking your account here.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default LinkedAccounts;
